import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';

// Async thunks
export const fetchBusinessKeywordsCategories = createAsyncThunk(
  'businessKeywordsCategories/fetchBusinessKeywordsCategories',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.businessKeywordsCategories.getAll(params);
      if (!response.data.status) return rejectWithValue(response.data.message);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch business keywords categories');
    }
  }
);

export const fetchBusinessKeywordsCategoryById = createAsyncThunk(
  'businessKeywordsCategories/fetchBusinessKeywordsCategoryById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.businessKeywordsCategories.getById(id);
      if (!response.data.status) return rejectWithValue(response.data.message);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch business keywords category');
    }
  }
);

export const createBusinessKeywordsCategory = createAsyncThunk(
  'businessKeywordsCategories/createBusinessKeywordsCategory',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.businessKeywordsCategories.create(data);
      if (!response.data.status) return rejectWithValue(response.data.message);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create business keywords category');
    }
  }
);

export const createBusinessKeywordsCategoriesBulk = createAsyncThunk(
  'businessKeywordsCategories/createBusinessKeywordsCategoriesBulk',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.businessKeywordsCategories.createBulk(data);
      if (!response.data.status) return rejectWithValue(response.data.message);
      return response.data.data; // array of created/updated links
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk create business keywords categories');
    }
  }
);

export const updateBusinessKeywordsCategory = createAsyncThunk(
  'businessKeywordsCategories/updateBusinessKeywordsCategory',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.businessKeywordsCategories.update(id, data);
      if (!response.data.status) return rejectWithValue(response.data.message);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update business keywords category');
    }
  }
);

export const deleteBusinessKeywordsCategory = createAsyncThunk(
  'businessKeywordsCategories/deleteBusinessKeywordsCategory',
  async (id, { rejectWithValue }) => {
    try {
      await api.businessKeywordsCategories.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete business keywords category');
    }
  }
);

export const bulkDeleteBusinessKeywordsCategories = createAsyncThunk(
  'businessKeywordsCategories/bulkDeleteBusinessKeywordsCategories',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await api.businessKeywordsCategories.bulkDelete(ids);
      if (!response.data.status) return rejectWithValue(response.data.message);
      return { ids, deleted: response.data.data?.deleted };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk delete business keywords categories');
    }
  }
);

export const bulkUpdateBusinessKeywordsCategoriesStatus = createAsyncThunk(
  'businessKeywordsCategories/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      const response = await api.businessKeywordsCategories.bulkUpdateStatus(ids, status);
      if (!response.data.status) return rejectWithValue(response.data.message);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update business keywords category status');
    }
  }
);

const businessKeywordsCategoriesSlice = createSlice({
  name: 'businessKeywordsCategories',
  initialState: {
    items: [],
    currentItem: null,
    loading: false,
    error: null,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10
    }
  },
  reducers: {
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSorting: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.sortBy = sortBy;
      state.sortOrder = sortOrder;
    },
    resetSorting: (state) => {
      state.sortBy = 'createdAt';
      state.sortOrder = 'desc';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch business keywords categories
      .addCase(fetchBusinessKeywordsCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessKeywordsCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.businessKeywordsCategories;
        state.pagination = action.payload.pagination;
        // Store current sorting parameters from the request
        const meta = action.meta.arg || {};
        state.sortBy = meta.sortBy || 'createdAt';
        state.sortOrder = meta.sortOrder || 'desc';
      })
      .addCase(fetchBusinessKeywordsCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch business keywords category by ID
      .addCase(fetchBusinessKeywordsCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessKeywordsCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
      })
      .addCase(fetchBusinessKeywordsCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create business keywords category
      .addCase(createBusinessKeywordsCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBusinessKeywordsCategory.fulfilled, (state, action) => {
        state.loading = false;
        // Handle multiple relationships being created
        if (Array.isArray(action.payload)) {
          state.items.unshift(...action.payload);
        } else {
          state.items.unshift(action.payload);
        }
      })
      .addCase(createBusinessKeywordsCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Bulk create business keywords categories
      .addCase(createBusinessKeywordsCategoriesBulk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBusinessKeywordsCategoriesBulk.fulfilled, (state, action) => {
        state.loading = false;
        const created = Array.isArray(action.payload) ? action.payload : [];
        // Prepend or merge by _id
        const existingById = new Map(state.items.map(i => [i._id, i]));
        for (const doc of created) {
          if (existingById.has(doc._id)) {
            existingById.set(doc._id, doc);
          } else {
            state.items.unshift(doc);
          }
        }
      })
      .addCase(createBusinessKeywordsCategoriesBulk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update business keywords category
      .addCase(updateBusinessKeywordsCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBusinessKeywordsCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentItem && state.currentItem._id === action.payload._id) {
          state.currentItem = action.payload;
        }
      })
      .addCase(updateBusinessKeywordsCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete business keywords category
      .addCase(deleteBusinessKeywordsCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBusinessKeywordsCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item._id !== action.payload);
        if (state.currentItem && state.currentItem._id === action.payload) {
          state.currentItem = null;
        }
      })
      .addCase(deleteBusinessKeywordsCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Bulk delete
      .addCase(bulkDeleteBusinessKeywordsCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteBusinessKeywordsCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => !action.payload.ids.includes(item._id));
      })
      .addCase(bulkDeleteBusinessKeywordsCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(bulkUpdateBusinessKeywordsCategoriesStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateBusinessKeywordsCategoriesStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateBusinessKeywordsCategoriesStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearCurrentItem, clearError, setSorting, resetSorting } = businessKeywordsCategoriesSlice.actions;
export default businessKeywordsCategoriesSlice.reducer;